import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";

import { SessionProvider } from "@/lib/session";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { AuthenticatedLayout } from "@/components/layout/AuthenticatedLayout";
import { LoginPage } from "@/routes/login";

type LoginSearch = {
  auth_error?: string;
  reason?: string;
  return_to: string;
};

function RootComponent() {
  return (
    <SessionProvider>
      <Outlet />
    </SessionProvider>
  );
}

const rootRoute = createRootRoute({
  component: RootComponent,
});

const publicLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "public",
  component: PublicLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    const KNOWN_AUTH_ERRORS = new Set([
      "access_denied",
      "invalid_request",
      "server_error",
      "temporarily_unavailable",
      "unauthorized_client",
      "unsupported_response_type",
    ]);

    const sanitize = (value: unknown, allowlist?: Set<string>): string | undefined => {
      if (typeof value !== "string") return undefined;
      const trimmed = value.slice(0, 128).trim();
      if (!trimmed) return undefined;
      if (allowlist && !allowlist.has(trimmed)) return undefined;
      return trimmed;
    };

    return {
      auth_error: sanitize(search.auth_error, KNOWN_AUTH_ERRORS),
      reason: sanitize(search.reason),
      return_to: typeof search.return_to === "string" ? search.return_to : "/dashboard",
    };
  },
  component: LoginPage,
});

const authenticatedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authenticated",
  component: AuthenticatedLayout,
});

const routeTree = rootRoute.addChildren([
  publicLayoutRoute.addChildren([loginRoute]),
  authenticatedLayoutRoute,
]);

export const router = createRouter({ routeTree });

export function AppRouterProvider() {
  return <RouterProvider router={router} />;
}
